"use client";
import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";
import Image from "next/image";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

const SafeImage = ({ src, fallbackSrc, ...props }) => {
  const [imgSrc, setImgSrc] = React.useState(src);
  React.useEffect(() => {
    setImgSrc(src);
  }, [src]);
  return (
    <Image alt="Odoo ERP Software"
      {...props}
      src={imgSrc}
      onError={() => {
        if (fallbackSrc) setImgSrc(fallbackSrc);
      }}
    />
  );
};

/* 1. Point of Sale & Order Management Section */
export function RestaurantPOSOrders({ section }) {
  return (
    <section className="py-16 md:py-24 relative overflow-hidden bg-transparent w-full font-sans text-center">
      {/* Warm red backdrop glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-rose-500/5 rounded-full blur-[120px] -z-10 pointer-events-none" />

      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        
        {/* Title & Copywriting block */}
        <div className="flex flex-col items-center mb-12">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`${caveat.className} text-5xl md:text-6xl lg:text-[70px] font-bold text-white leading-tight mb-6`}
          >
            Take your <span className="x_wd_green_highlight_03 inline-block">orders</span> with precision
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto"
          >
            <strong>Point of Sale and kitchen display integration.</strong> Easily manage tables and take orders. Quickly navigate the floor plan to add orders, create multi-course menus with customizable combos, and send orders directly to the kitchen for seamless communication.
          </motion.p>
        </div>

        {/* Triple Mockup Composition matching Odoo's exact layout */}
        <div className="row align-items-center justify-content-center py-12 relative">
          
          {/* Main Large Tablet/POS mockup centered - col-lg-7 */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="col-lg-7 position-relative flex justify-center z-10"
          >
            <div className="p-1.5 bg-white border border-gray-200 rounded-[24px] shadow-2xl hover:scale-[1.01] transition-transform duration-300 w-full max-w-[620px]">
              <Image
                src="/odoo/industries/fine-dining-restaurant/take-your-orders-with-precision-3.webp"
                width={620}
                height={420}
                alt="POS Main Layout"
                className="w-full h-auto rounded-[18px] object-cover"
              />
            </div>
          </motion.div>

          {/* Overlapping Small Mockup Left - d-none d-lg-block */}
          <motion.div
            initial={{ opacity: 0, x: -30, y: 20 }}
            whileInView={{ opacity: 1, x: 0, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="col-lg-3 d-none d-lg-block position-absolute z-20"
            style={{ left: "5%", bottom: "10%" }}
          >
            <div className="p-1.5 bg-white border border-gray-200 rounded-[20px] shadow-2xl hover:scale-[1.02] transition-transform duration-300 max-w-[280px]">
              <Image
                src="/odoo/industries/fine-dining-restaurant/take-your-orders-with-precision-1.webp"
                width={280}
                height={200}
                alt="POS Table Map"
                className="w-full h-auto rounded-[14px] object-cover"
              />
            </div>
          </motion.div>

          {/* Overlapping Small Mockup Right - d-none d-lg-block */}
          <motion.div
            initial={{ opacity: 0, x: 30, y: -20 }}
            whileInView={{ opacity: 1, x: 0, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="col-lg-3 d-none d-lg-block position-absolute z-20"
            style={{ right: "5%", bottom: "25%" }}
          >
            <div className="p-1.5 bg-white border border-gray-200 rounded-[20px] shadow-2xl hover:scale-[1.02] transition-transform duration-300 max-w-[280px]">
              <Image
                src="/odoo/industries/fine-dining-restaurant/take-your-orders-with-precision-2.webp"
                width={280}
                height={200}
                alt="POS Billing Details"
                className="w-full h-auto rounded-[14px] object-cover"
              />
            </div>
          </motion.div>

        </div>

      </div>
    </section>
  );
}

/* 2. Table Reservations & Scheduling Section */
export function RestaurantTableBooking({ section }) {
  return (
    <section className="pb-16 md:py-24 relative overflow-hidden bg-transparent w-full font-sans text-start">
      {/* Soft emerald backdrop glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-[120px] -z-10 pointer-events-none" />

      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">

        {/* Speech Bubble / Persona placed at the top matching Odoo design */}
        <div className="s_wd_npc pb-8 pt-4">
          <div className="d-flex align-items-center justify-content-center">
            <Image
              src="/odoo/arrows/grey_arrow_loop_02.svg"
              width={140}
              height={140}
              style={{ width: "140px", height: "auto" }}
              className="d-none d-sm-block rotate-50 flip mt-7 me-3"
              alt=""
            />
            <SafeImage
              width={60}
              height={60}
              className="d-none d-md-inline-block rounded-circle me-2"
              alt="Bella"
              src="/odoo/snippets/s_wd_persona/bella.webp"
              fallbackSrc="/odoo/snippets/s_wd_persona/bob.webp"
            />
            <div className="bg-white rounded rounded-pill-sm shadow p-3 px-sm-5 text-gray-800 font-medium border border-gray-100">
              <i>My usual table, please!</i>
            </div>
          </div>
        </div>

        {/* Main Grid Row matching Odoo's nesting */}
        <div className="row gx-5 justify-content-between">
          
          {/* Header Column - col-lg-8 */}
          <div className="col-lg-8 pb-7">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className={`${caveat.className} text-5xl md:text-6xl lg:text-[70px] font-bold text-white leading-tight mb-6 flex flex-wrap items-center gap-3`}
            >
              Table <span className="x_wd_yellow_highlight_02 inline-block">booking</span> made simple
              <SafeImage
                src="/odoo/icons/emoticon-smile.svg"
                width={100}
                height={100}
                className="d-none d-lg-inline-block rotate-10 ms-3 mt-n3 select-none pointer-events-none"
                alt="Smile"
                fallbackSrc="https://odoocdn.com/openerp_website/static/src/img/icons/emoticon-smile.svg"
              />
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-xl text-gray-300 leading-relaxed"
            >
              <strong>Online table booking.</strong> Customers can book tables online with Odoo Appointments. Reserved tables appear on your customizable floor plan, which can be easily edited with the graphical editor.
            </motion.p>
          </div>

          {/* Nested Mockup Composition Row */}
          <div className="row position-relative x_wd_shape x_wd_shape_rs x_wd_shape_ol pb-5">
            
            {/* Left Mockup Column - col-lg-6 */}
            <motion.div
              initial={{ opacity: 0, x: -35 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="col-lg-6 mt-n5 flex flex-col"
            >
              <div className="p-1.5 bg-white border border-gray-200 rounded-[24px] shadow-2xl hover:scale-[1.01] transition-transform duration-300 w-full mb-3">
                <SafeImage
                  src="/odoo/industries/fine-dining-restaurant/table-booking-made-simple.webp"
                  width={600}
                  height={400}
                  alt="Table Booking Floor Plan"
                  className="w-full h-auto rounded-[18px] object-cover"
                  fallbackSrc="https://odoocdn.com/openerp_website/static/src/img/industries/fine-dining-restaurant/table-booking-made-simple.webp"
                />
              </div>
              <Image
                src="/odoo/arrows/arrow_xl_02.svg"
                width={100}
                height={100}
                alt=""
                className="d-none d-lg-block rotate-90 flip ms-auto select-none pointer-events-none"
              />
            </motion.div>

            {/* Right Mockup Column - col-lg-6 */}
            <motion.div
              initial={{ opacity: 0, x: 35 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="col-lg-6 mt-lg-n5 flex flex-col justify-between"
            >
              <div className="p-1.5 bg-white border border-gray-200 rounded-[20px] shadow-2xl hover:scale-[1.01] transition-transform duration-300 w-full mb-4 d-none d-lg-block">
                <SafeImage
                  src="/odoo/industries/fine-dining-restaurant/table-booking-made-simple-1.webp"
                  width={600}
                  height={400}
                  alt="Edit Booking Dialog"
                  className="w-full h-auto rounded-[14px] object-cover"
                  fallbackSrc="https://odoocdn.com/openerp_website/static/src/img/industries/fine-dining-restaurant/table-booking-made-simple-1.webp"
                />
              </div>
              <div className="p-1.5 bg-white border border-gray-200 rounded-[20px] shadow-2xl hover:scale-[1.01] transition-transform duration-300 w-full">
                <SafeImage
                  src="/odoo/industries/fine-dining-restaurant/table-booking-made-simple-2.webp"
                  width={600}
                  height={400}
                  alt="Date Selection Calendar"
                  className="w-full h-auto rounded-[14px] object-cover"
                  fallbackSrc="https://odoocdn.com/openerp_website/static/src/img/industries/fine-dining-restaurant/table-booking-made-simple-2.webp"
                />
              </div>
            </motion.div>

          </div>

        </div>

      </div>
    </section>
  );
}

/* 3. Automatic Replenishment Section */
export function RestaurantReplenishment({ section }) {
  return (
    <section className="py-16 md:py-24 relative overflow-hidden bg-transparent w-full font-sans text-center">
      {/* Soft yellow backdrop glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-yellow-500/5 rounded-full blur-[120px] -z-10 pointer-events-none" />

      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        
        <div className="row justify-content-center">
          
          {/* Title Block - col-lg-12 with proper flex gap and alignment */}
          <div className="col-lg-12 flex justify-center items-center mb-8 gap-4 md:gap-8">
            <SafeImage
              src="/odoo/icons/yellow_unbox.svg"
              width={70}
              height={70}
              className="d-none d-lg-inline mb-n3 rotate-200 select-none pointer-events-none"
              alt=""
              fallbackSrc="/odoo/icons/green_unbox.svg"
            />
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className={`${caveat.className} text-5xl md:text-6xl lg:text-[75px] font-bold text-white leading-tight mb-0`}
            >
              Never ever run <span className="x_wd_yellow_highlight_bold_03 inline-block">out of stock</span>
            </motion.h2>
            <SafeImage
              src="/odoo/icons/yellow_unbox.svg"
              width={70}
              height={70}
              className="d-none d-lg-inline mb-4 rotate-10 select-none pointer-events-none"
              alt=""
              fallbackSrc="/odoo/icons/green_unbox.svg"
            />
          </div>

          {/* Description Block - col-lg-10 col-xl-8 with extra bottom padding to increase gap */}
          <div className="col-lg-10 col-xl-8 pb-10 mx-auto">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-xl text-gray-300 leading-relaxed mb-0"
            >
              <strong>Automated and manual replenishment.</strong> Automate purchase orders for trackable items and use recurring tasks for manual tracking. Easily create, track, and validate purchase orders to maintain up-to-date stock levels.
            </motion.p>
          </div>

          {/* Image Mockup Block - col-xl-10 with perfect vertical spacing */}
          <div className="col-xl-10 mx-auto position-relative pt-6 pb-14">
            {/* Centered circular contrast shape with border removed and size decreased */}
            <div className="absolute top-[50%] left-[50%] -translate-x-1/2 -translate-y-1/2 w-[450px] h-[450px] bg-white/[0.03] rounded-full -z-10 pointer-events-none" />

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="w-full max-w-[950px] mx-auto"
            >
              {/* White card layout with blue ray sparkles highlights */}
              <div className="p-1.5 bg-white border border-gray-200 rounded-[24px] shadow-2xl hover:scale-[1.01] transition-transform duration-300 relative z-10 x_wd_corner_highlight_03">
                <Image
                  src="/odoo/industries/fine-dining-restaurant/never-ever-run-out-of-stock.webp"
                  width={950}
                  height={600}
                  alt="Stock replenishment"
                  className="w-full h-auto rounded-[18px] object-cover"
                />
              </div>
            </motion.div>
          </div>

        </div>

      </div>
    </section>
  );
}

/* 4. Scheduling & Planning Section */
export function RestaurantScheduling({ section }) {
  return (
    <section className="py-16 md:py-24 relative overflow-hidden bg-transparent w-full font-sans text-start">
      {/* Soft blue-purple backdrop glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-sky-500/5 rounded-full blur-[120px] -z-10 pointer-events-none" />

      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        <div className="row g-5 align-items-center justify-between">

          {/* Left Column: Image mockup - col-lg-6 with relative positioning */}
          <motion.div
            initial={{ opacity: 0, x: -35 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="col-lg-6 relative flex justify-center py-6"
          >
            {/* Left-aligned dome backdrop shape bubble constrained strictly to the image column with increased height */}
            <div className="absolute top-[-40px] bottom-[-40px] left-[-40%] right-[5%] bg-white/[0.03] rounded-r-[220px] -z-10 pointer-events-none" />

            <div className="p-1.5 bg-white border border-gray-200 rounded-[24px] shadow-2xl hover:scale-[1.01] transition-transform duration-300 w-full max-w-[580px] relative z-10">
              <Image
                src="/odoo/industries/fine-dining-restaurant/plan-your-services-like-a-pro.webp"
                width={580}
                height={400}
                alt="Planning Shifts Mockup"
                className="w-full h-auto rounded-[18px] object-cover"
              />
            </div>
          </motion.div>

          {/* Right Column: Title, Clock Icon & Paragraphs - col-lg-5 */}
          <motion.div
            initial={{ opacity: 0, x: 35 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="col-lg-5 offset-lg-1 flex flex-col gap-6"
          >
            {/* Clock Graphics Doodle */}
            <div className="flex justify-start mb-4">
              <Image
                src="/odoo/graphics/clock.svg"
                width={180}
                height={180}
                className="select-none pointer-events-none"
                alt="Clock"
              />
            </div>

            <h2 className={`${caveat.className} text-5xl md:text-6xl lg:text-[70px] font-bold text-white leading-tight mb-6`}>
              Plan your services like <span className="x_wd_red_highlight_05 inline-block">a pro</span>
            </h2>

            <div className="space-y-6">
              <p className="text-xl text-gray-300 leading-relaxed">
                <strong>Smart shift scheduling.</strong> Schedule shifts and share them with employees. Automatically assign or manually adjust shifts based on availability. Manage roles and ensure every shift is covered, keeping your team informed and ready.
              </p>
            </div>
          </motion.div>

        </div>

      </div>
    </section>
  );
}
